import { resolveFeatureGate } from 'pb-billing-ui/feature-gates.ts';
import { usePersonStore } from '~/stores/person';
import { LicenseRenewalTerm, LicenseRenewalTermDisplay, LicenseRenewalTermKey } from '~/types/licenses/licenses';
import { licenseTierName, licenseTierRank } from '~/types/licenses/tiers';

/**
 * The other half of an upgrade that happened in checkout: the member lands back on the page they
 * left, and something has to say what turned on.
 *
 * Checkout writes hints on the return URL: `upgraded`, the plan slug a change that is active now
 * moved the licence to, or `scheduled`, the plan a change that takes effect at renewal will move it
 * to; `term`, the renewal term a change active now moved it to; `change=term` when that change
 * moved ONLY the term, or `change=keep` when the member now pays for the plan another member's
 * extended to them; `license`, the id of the licence it changed; and `unlocked`, the feature
 * gate that sent them. All are hints: the notice is shown only when the LOADED licence confirms
 * them — held, on that plan and on that term for `upgraded`, no longer extended by anyone for
 * `change=keep`, carrying that plan as its scheduled change for `scheduled` — so a stale link,
 * a back button or a cancelled purchase cannot congratulate someone on a plan they do not have.
 * The licence is found by id across every licence the member holds, because a change to an
 * additional licence must be confirmed against that row and not against the primary; a link with
 * no id is read against the primary. `unlocked` is resolved through the shared registry, and a
 * value it does not publish is simply dropped rather than printed.
 *
 * The params are stripped before anything else, so the notice cannot fire twice — and the return
 * never reopens the modal: the member has already bought.
 */

/**
 * Every hint checkout writes on the return URL. Read once here and stripped; also stripped from
 * the path the NEXT checkout returns to (useLicensePurchase), so an outcome never rides along
 * into a later return and gets read ahead of the fresh one.
 */
export const UPGRADE_RETURN_PARAMS = ['upgraded', 'scheduled', 'term', 'change', 'license', 'unlocked'] as const;

export const useUpgradeReturnNotice = () => {
    const route = useRoute();
    const router = useRouter();
    const personStore = usePersonStore();
    const { showSuccess } = useGlobalToast();

    // One shot per page visit, like usePurchaseTriggers: the params are gone after the first run.
    let hasRun = false;

    /**
     * The scope this composable was set up in, captured HERE and not where it is used.
     *
     * `whenPersonLoaded()` runs after an await, and Vue restores the previous scope synchronously
     * when the call that set it up returns — so by the time the continuation runs there is no
     * active scope, and an `onScopeDispose` there registers nothing at all. Holding the scope is
     * what lets a watcher created later still be torn down with the page.
     */
    const scope = getCurrentScope();

    /** A query value is `string | string[] | null`; the first string wins, anything else is null. */
    function firstString(value: unknown): string | null {
        const candidate = Array.isArray(value) ? value[0] : value;
        return typeof candidate === 'string' && candidate ? candidate : null;
    }

    function whenPersonLoaded(): Promise<void> {
        if (personStore.person) return Promise.resolve();

        return new Promise((resolve) => {
            const stop = watch(
                () => personStore.person?.id,
                (id) => {
                    if (!id) return;
                    stop();
                    resolve();
                },
            );
            // A person that never loads would otherwise leave this watcher running for the life
            // of the page. Leaving the page it was set up on is what says nobody is waiting.
            scope?.run(() => onScopeDispose(stop));
        });
    }

    async function stripReturnParams() {
        const query = { ...route.query };
        let changed = false;
        for (const key of UPGRADE_RETURN_PARAMS) {
            if (key in query) {
                delete query[key];
                changed = true;
            }
        }
        // The fragment is carried through. Checkout writes the outcome BEFORE the fragment, so
        // the member arrives on the anchor they left from, and a `replace` that names only the
        // query blanks the hash, which would throw that anchor away at the last step.
        if (changed) await router.replace({ query, hash: route.hash });
    }

    async function runUpgradeReturnNotice() {
        if (hasRun) return;
        hasRun = true;

        const upgraded = firstString(route.query.upgraded);
        const scheduled = firstString(route.query.scheduled);
        const term = firstString(route.query.term);
        const changeHint = firstString(route.query.change);
        const termOnly = changeHint === 'term';
        const kept = changeHint === 'keep';
        const licenseId = firstString(route.query.license);
        const gate = resolveFeatureGate(firstString(route.query.unlocked));

        await stripReturnParams();

        if (!upgraded && !scheduled) return;

        await whenPersonLoaded();

        // The loaded licence is the only thing that can confirm the change landed — the one the
        // link names, wherever the member holds it, or the primary for a link that names none.
        const person = personStore.person;
        const held = [person?.license, ...(person?.licenses ?? [])].filter((l): l is NonNullable<typeof l> => !!l);
        const license = licenseId ? held.find((l) => String(l.id) === licenseId) : person?.license;
        if (!license) return;

        if (upgraded) {
            if (license.expired || license.slug !== upgraded) return;
            // A term named by the link is confirmed too — it is the whole change when the plan
            // did not move, and an unchanged plan on the old term confirms nothing.
            if (term && LicenseRenewalTermKey[license.term] !== term) return;

            // The payment that makes the seat the member's own is what ends its sponsorship, so a
            // licence still extended by someone confirms nothing.
            if (kept) {
                if (license.sponsored) return;
                showSuccess(`${licenseTierName(upgraded)} is yours now`, "It no longer depends on anyone else's plan.", {
                    timeout: 8000,
                });
                return;
            }

            if (termOnly) {
                showSuccess(
                    `Your ${licenseTierName(upgraded)} plan's term has changed`,
                    `Your new term is ${LicenseRenewalTermDisplay[license.term]}.`,
                    { timeout: 8000 },
                );
                return;
            }

            // The feature is named only when the plan just confirmed actually carries it. The
            // hint is untrusted and can arrive stale or hand-edited — `upgraded=pro&unlocked=
            // threat-hunting` — and a Pro licence confirms nothing about a Signature feature.
            const carries = !!gate && licenseTierRank(license.slug) >= licenseTierRank(gate.requiredPlan);

            showSuccess(
                `Your ${licenseTierName(upgraded)} plan has started`,
                carries ? `${gate.label} is now on.` : 'Your plan is upgraded.',
                { timeout: 8000 },
            );
            return;
        }

        // A scheduled change is confirmed by the licence carrying it as its own scheduled change;
        // the plan on the licence has not moved yet, and must not be read as though it had. A
        // term named by the link is confirmed against the scheduled term the same way — for a
        // same-plan change it is the whole change, and the plan alone would confirm any of them.
        const change = license.scheduled_change;
        if (change?.license?.slug !== scheduled) return;
        const scheduledKey = (Object.keys(LicenseRenewalTermKey) as unknown as LicenseRenewalTerm[])
            .find((months) => Number(months) === Number(change.term));
        const scheduledTermKey = scheduledKey !== undefined ? LicenseRenewalTermKey[scheduledKey] : undefined;
        if (term && scheduledTermKey !== term) return;

        if (termOnly && scheduledKey !== undefined) {
            showSuccess(
                `Your ${licenseTierName(scheduled!)} plan's term change is scheduled`,
                `It moves to ${LicenseRenewalTermDisplay[scheduledKey]} at your next renewal.`,
                { timeout: 8000 },
            );
            return;
        }

        showSuccess(
            `Your change to ${licenseTierName(scheduled!)} is scheduled`,
            'It takes effect at your next renewal.',
            { timeout: 8000 },
        );
    }

    return { runUpgradeReturnNotice };
};
